
import { Vue, Options } from "vue-class-component";
import ScanTableSection from "../components/scan-table-section.vue";
import {
  BookingPayloadData,
  FlightRequest,
  FormCargoV2,
  RtcData
} from "@/domain/entities/CargoV2";
import CreateCargoRtcLayout from "./create-cargo-rtc-layout.vue";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  formatDateNormal,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  CreateCargoV3ApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import CargoDisputeForm from "../components/cargo-dispute-form/index.vue";
import CargoV2ManualInput from "../components/manual-input/cargo-v2-manual-input.vue";
import {
  ModalMessageEntities,
  OptionsClass,
  Snackbar
} from "@/domain/entities/MainApp";
import { CALCULATE_TYPE } from "../modules/cargo-module";
import { CheckEligible, useCargoV2 } from "../composables";
import { isPlaneNogValid } from "../utils";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { AccountController } from "@/app/ui/controllers/AccountController";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    ScanTableSection,
    CreateCargoRtcLayout,
    CargoDisputeForm,
    CargoV2ManualInput
  },
  beforeRouteLeave(to, _from, next) {
    if (to.name !== "cargo-v2-create-rtc-edit-search-flight") {
      CargoV2Controller.setRtcDetail(new RtcData());
    }
    next();
  },
  watch: {
    isUploadProofPhoto: {
      handler(bool: boolean) {
        if (!bool) CargoV2Controller.setForm({ photoProof: [] });
      }
    }
  }
})
class CargoV2CreateRtc extends Vue {
  showForm = false;

  get rtcDetailData() {
    return new RtcData(CargoV2Controller.rtcDetail);
  }

  get isPlane() {
    return this.form.cargoType.value === "plane";
  }

  get isTruck() {
    return this.form.cargoType.value === "truck";
  }

  get isShip() {
    return this.form.cargoType.value === "ship";
  }

  get isValidShip() {
    if (!this.isShip) return true;
    return (
      this.form.cargoNo &&
      (this.form.driverSelected === "other"
        ? this.form.isValidVehicleNo
        : this.form.driverSelected)
    );
  }

  get isValidTruck() {
    if (!this.isTruck) return true;
    return this.form.driverSelected === "other"
      ? this.form.isValidVehicleNo
      : this.form.driverSelected;
  }

  get isValidPlane() {
    if (!this.isPlane) return true;
    if (this.form.cargoNo) return this.form.isValidAwbManual;
    return true;
  }

  get isUploadProofPhoto() {
    return CargoV2Controller.isUploadProofPhoto;
  }

  get isFormNotValid() {
    return (
      !this.form.actualTotalCargoGrossweigth ||
      !this.form.actualTotalCargoVolumeweight ||
      !this.form.actualDimension.cargoActualHeightDimension ||
      !this.form.actualDimension.cargoActualLengthDimension ||
      !this.form.actualDimension.cargoActualWidthDimension ||
      !this.form.cargoActualPiece ||
      !this.form.cargoCommodity.value ||
      !this.form.productCargo ||
      (this.isUploadProofPhoto
        ? !this.form.photoProof
            .filter(Boolean)
            .filter((p) => typeof p === "string").length
        : false) ||
      CargoV2Controller.isBagOnlyExceedMaxDisputeWeight ||
      CargoV2Controller.isExceedMaxDisputeWeight ||
      CargoV2Controller.isGrossWeightInputError ||
      CargoV2Controller.isVolumeWeightError ||
      CargoV2Controller.isPiecesInputError ||
      !this.isValidPlane ||
      !this.isValidTruck ||
      !this.isValidShip ||
      !isPlaneNogValid(this.form.cargoType.value, this.form.nog)
    );
  }

  get disabledSelectSchedule() {
    if (CargoV2Controller.isLoadingCalculateVolume) return true;
    return !!this.form.cargoNo || this.isFormNotValid;
  }

  get disabledBooking() {
    if (
      this.isPlane &&
      this.rtcDetailData.cargoType !== "plane" &&
      !this.form.isValidAwbManual
    )
      return true;
    if (
      this.isPlane &&
      this.rtcDetailData.cutOffTimeExceededWithoutNextConfig &&
      new Date(this.rtcDetailData.nextCargoFlightTime).getFullYear() === 1 &&
      !this.form.isValidAwbManual
    )
      return true;
    if (CargoV2Controller.isLoadingCalculateVolume) return true;
    return this.isFormNotValid;
  }

  get disabledChangeTransport() {
    return (
      CargoV2Controller.isLoadingCalculateVolume ||
      CargoV2Controller.isBagOnlyExceedMaxDisputeWeight ||
      CargoV2Controller.isExceedMaxDisputeWeight
    );
  }

  get showSelectSchedule() {
    if (!this.form.cargoType?.value) return false;
    return this.isPlane;
  }

  get form() {
    return new FormCargoV2(CargoV2Controller.form);
  }

  get calculateType() {
    return CargoV2Controller.calculateType;
  }

  get flightData() {
    if (!this.isPlane)
      return [
        new FlightRequest({
          flightOrigin: this.rtcDetailData.flightsData[0]?.airportCodeOrigin,
          flightDestination: this.rtcDetailData.flightsData[0]
            ?.airportCodeDestination,
          flightDateTime: this.departureDate
        })
      ];
    return CargoV2Controller.flightsData;
  }

  get departureDate() {
    if (this.isTruck || this.isShip)
      return formatDateNormal(new Date(), "YYYY-MM-DD HH:mm:ss");
    if (this.isPlane && this.form.cargoNo && this.form.cargoVehicleNo)
      return this.form.departureDate;
    return CargoV2Controller.departureDate;
  }

  get arrivalDate() {
    if (this.isTruck || this.isShip)
      return formatDateNormal(
        new Date(new Date().setDate(new Date().getDate() + 6)),
        "YYYY-MM-DD HH:mm:ss"
      );
    if (this.isPlane && this.form.cargoNo && this.form.cargoVehicleNo)
      return this.form.arrivalDate;
    return CargoV2Controller.arrivalDate;
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  async mounted() {
    CargoV2Controller.setIsFirstLandingRtc(false);
    MainAppController.showLoading();
    await this.mappingRtcDetail();
    await this.$nextTick();
    this.showForm = true;
    MainAppController.closeLoading();
    this.$router.replace({
      query: {
        params: `${this.form.originCityCode}-${this.form.destinationCityCode
          .code || this.form.destinationCityCode.value}`
      }
    });
    const el = document.querySelector(
      `[data-content="${this.$route.params.id}"]`
    );
    if (el) el.remove(); // remove breadcrumb
  }

  async getDetailRtc() {
    try {
      MainAppController.showLoading();
      const id = this.$route.params.id as string;
      CargoV2Controller.getDetailReadyToCargo({
        id: Number(id),
        flow: "rtc-edit"
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargoV2.create.rtc.error.getDetailRtc"),
          () => this.getDetailRtc(),
          () => this.$router.push("/cargo-v2/create")
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async mappingRtcDetail() {
    const isEmptyDetailData = !this.rtcDetailData.id;
    if (isEmptyDetailData) await this.getDetailRtc();
    await this.$nextTick();
    await new Promise((resolve) => {
      // this only for generate list commodity based on dominan, for set form is on the next generate commodity
      CargoV2Controller.onGenerateDominanCommodityGroup(false);

      CargoV2Controller.setForm({
        bookingPayload: new BookingPayloadData({
          awbNo: "",
          shcCode: this.form.shc,
          flightRequest: this.flightData
        })
      });

      if (!isEmptyDetailData) return resolve(true);
      const cargoType = this.form.cargoType.value
        ? this.form.cargoType
        : new OptionsClass({
            value: CargoV2Controller.rtcDetail.cargoType
          });
      const cargoCommodity = this.form.cargoCommodity.value
        ? this.form.cargoCommodity
        : new OptionsClass({
            name: CargoV2Controller.rtcDetail.commodityName,
            code: CargoV2Controller.rtcDetail.commodityCode,
            value: CargoV2Controller.rtcDetail.commodityCode
          });
      let bagOrStt = this.rtcDetailData.bagOrStt.slice();
      if (this.form.bagOrStt.length) bagOrStt = this.form.bagOrStt.slice();
      CargoV2Controller.onGenerateGroupDataTable(bagOrStt);
      this.$nextTick().then(() => {
        CargoV2Controller.setForm({
          destinationCityCode: new OptionsClass({
            name: this.rtcDetailData.destinationName,
            code: this.rtcDetailData.destinationCode,
            value: this.rtcDetailData.destinationCode,
            countryCode: this.rtcDetailData.destinationCountryCode
          }),
          actualTotalCargoGrossweigth: this.form
            .totalAccumulationEstimationGrossWeightDataTable,
          actualTotalCargoVolumeweight: this.form
            .totalAccumulationEstimationGrossWeightDataTable,
          cargoActualPiece: this.form.totalAccumulationActualPieceDataTable,
          cargoType,
          cargoCommodity,
          cargoRtcTurnAuto: !this.form.isValidAwbManual,
          isCargoPlaneManual: this.form.isValidAwbManual,
          cargoOverCutoffLimit: CargoV2Controller.cargoOverCutoffLimit
        });
        CargoV2Controller.onGenerateDominanCommodityGroup();
        CargoV2Controller.onGenerateDefaultNog();
        this.$nextTick().then(resolve);
      });
    });

    await onCheckEligible(
      new CheckEligible({
        success: () => {
          this.$nextTick(() => {
            this.calculateVolume(
              typeof this.calculateType !== "string"
                ? CALCULATE_TYPE.INIT
                : this.calculateType
            );
          });
        },
        close: () => this.checkExistBagOrStt()
      })
    );
  }

  checkExistBagOrStt() {
    this.$nextTick().then(() => {
      if (this.form.bagOrStt.length) {
        this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
        this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
        this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
        CargoV2Controller.setForm(this.form);
        this.$nextTick().then(async () => {
          await this.calculateVolume(CALCULATE_TYPE.INIT);
        });
      } else {
        this.$router.push("/cargo-v2/create");
      }
    });
  }

  onChangeCargoType(cargoType: string) {
    onCheckEligible(
      new CheckEligible({
        success: () => this.changeCargoType(cargoType),
        close: () => this.checkExistBagOrStt()
      })
    );
  }

  changeCargoType(cargoType: string) {
    CargoV2Controller.setForm({
      cargoType: new OptionsClass({ value: cargoType }),
      cargoNo: "",
      driverSelected: "",
      cargoVehicleNo: "",
      isValidVehicleNo: false,
      isValidAwbManual: false
    });
    this.calculateVolume(CALCULATE_TYPE.CHANGE_MODA);
  }

  checkBooking() {
    onCheckEligible(
      new CheckEligible({
        success: () => this.onBooking(),
        close: () => this.checkExistBagOrStt()
      })
    );
  }

  async onBooking() {
    try {
      window.requestAnimationFrame(() => {
        MainAppController.showLoading();
      });
      await this.calculateVolume(this.calculateType);
      const resp = await CargoV2Controller.createBooking(
        new CreateCargoV3ApiRequest({
          ...this.form,
          rtcId: this.rtcDetailData.id,
          cargoNo:
            this.isShip || (this.isPlane && this.form.bookingPayload.awbNo)
              ? this.form.cargoNo
              : "",
          cargoRtcTurnAuto: false,
          isCargoPlaneManual: !!this.form.bookingPayload.awbNo,
          isFromRtc: true,
          isUseNewCalculation: true,
          departureDate: this.departureDate,
          arrivalDate: this.arrivalDate,
          destinationAirportCode: this.form.bookingPayload.awbNo
            ? this.form.destinationAirportCode
            : this.flightData[this.flightData.length - 1].flightDestination,
          originAirportCode: this.form.bookingPayload.awbNo
            ? this.form.originAirportCode
            : this.flightData[0].flightOrigin,
          route: `${this.form.originCityCode}-${this.form.destinationCityCode.code}`,
          totalPiece: this.form.totalAccumulationActualPieceDataTable,
          totalStt: this.form.bagOrStt.length,
          bookingPayload: !this.isPlane ? undefined : this.form.bookingPayload,
          calculateType: this.calculateType,
          cargoOverCutoffLimit: CargoV2Controller.cargoOverCutoffLimit
        })
      );
      if (resp.bookingRequestId && !this.form.cargoNo && this.isPlane) {
        const popupIllustration = "on-process-booking-cargo";
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: this.$t("cargoV2.create.modal.bookingProcessing.title", {
              bookingRequestId: resp.bookingRequestId
            }),
            message: this.$t("cargoV2.create.modal.bookingProcessing.message"),
            textSuccess: this.$t("cargoV2.create.common.okUpper"),
            image: popupIllustration,
            onSubmit: () => {
              MainAppController.closeMessageModal();
              this.$router.push("/cargo-v2/create");
            }
          })
        );
        return;
      }
      this.$router.push("/cargo-v2/create");
      window.requestAnimationFrame(() => {
        MainAppController.setSnackbarData(
          new Snackbar({
            type: "success",
            message: this.$t("cargoV2.create.snackbar.booked", {
              cargoNo: resp.cargoNo,
              destination: this.form.destinationCityCode.code
            })
          })
        );
        MainAppController.setAutoClose(true);
        MainAppController.setSnackbar(true);
      });
    } catch (error) {
      if (!(error as any).response) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            error,
            this.$t("cargoV2.create.rtc.error.bookingFailed"),
            () => this.onBooking(),
            () => this.$router.push("/cargo-v2/booking-cancelled")
          )
        );
        return;
      }
      const errMessage = parsingErrorResponse(error).message;
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          imageWidth: "32",
          imageHeight: "32",
          customWidth: "417px",
          title: this.$t("cargoV2.create.modal.bookingFailed.title"),
          message: this.$t("cargoV2.create.modal.bookingFailed.message", {
            origin: this.form.originCityCode,
            destination: this.form.destinationCityCode.code,
            reason: errMessage
          }),
          textSuccess: this.$t("cargoV2.create.common.ok"),
          image: "circle-error",
          customButton: "w-full",
          customParentButton: "w-full",
          customWrapperButton: "w-full px-6",
          onSubmit: () => {
            CargoV2Controller.setForm(new FormCargoV2());
            MainAppController.closeMessageModal();
            this.$router.push("/cargo-v2/booking-cancelled");
          }
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onSelectSchedule() {
    onCheckEligible(
      new CheckEligible({ success: this.redirectSelectSchedule })
    );
  }

  redirectSelectSchedule() {
    CargoV2Controller.setForm({
      cargoOverCutoffLimit: CargoV2Controller.cargoOverCutoffLimit
    });
    this.$router.push(
      `/cargo-v2/create/rtc/${this.$route.params.id}/search-flight`
    );
  }

  async calculateVolume(type: string) {
    await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: type,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          currentGrossWeight: this.form.actualTotalCargoGrossweigth,
          height: this.form.actualDimension.cargoActualHeightDimension,
          length: this.form.actualDimension.cargoActualLengthDimension,
          width: this.form.actualDimension.cargoActualWidthDimension,
          totalPieces: this.form.cargoActualPiece,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable
        })
      })
    );
    await this.$nextTick();
    await this.$nextTick();
    await this.$nextTick();
    CargoV2Controller.setForm(this.form);
  }
}
export default CargoV2CreateRtc;
